import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils.ts'
import { getTopBannerOffsetPx } from '@/routes/root/view/topBannerLayout.ts'

type Props = {
  visible: boolean
  bannerIndex?: number
}

/**
 * Ambient tier: a 2px indeterminate hairline, no words, no reserved space.
 *
 * This replaced a full-width 33px text banner ("Refreshing data in
 * background…"). The gate is an UNSCOPED `useIsFetching()`, so it fires on
 * essentially every navigation and every refetch — and because the banner
 * deliberately reserved no layout room, each of those appearances landed
 * ON TOP of content. Measured 2026-08-26 at 375px: it covered 32 of the
 * mobile topbar's 53px, 60% of it, including the back affordance.
 *
 * Routine background work does not deserve a sentence, a colour, or a row
 * of the viewport. It deserves the universal "something is loading" hint
 * and nothing more. The 2px strip rides at the bottom edge of whatever
 * status banners are reserved above it, so it never hides a control.
 *
 * The label survives for assistive tech but WITHOUT a live region: the old
 * `role="status" aria-live="polite"` re-announced on every refetch, which
 * is the same nagging problem one layer down. It stays readable in the
 * reading order, it just no longer interrupts.
 */
export default function BackgroundSyncIndicator({ visible, bannerIndex = 0 }: Props) {
  const { t } = useTranslation()

  if (!visible) return null

  return (
    <div
      data-cy="background-sync-indicator"
      className={cn(
        'pointer-events-none fixed left-0 right-0 z-[115] h-0.5 overflow-hidden',
        'top-[calc(var(--safe-area-inset-top)+var(--top-banner-offset,0px))]',
        'md:top-[var(--top-banner-offset,0px)]',
        'md:left-[var(--sidebar-width)]',
      )}
      style={
        {
          '--top-banner-offset': `${getTopBannerOffsetPx({ bannerIndex })}px`,
        } as CSSProperties
      }
    >
      <span className="sr-only">{t('sync.backgroundRefreshing')}</span>
      <div
        aria-hidden="true"
        className={cn(
          'h-full w-1/3 rounded-full bg-slate-400/80 dark:bg-slate-500',
          'animate-indeterminate-progress',
          // Reduced motion gets a static, dimmer strip: the signal stays,
          // the movement that carries it does not.
          'motion-reduce:w-full motion-reduce:animate-none motion-reduce:opacity-50',
        )}
      />
    </div>
  )
}
